/**
 * SEVERITY — ok / follow_up / incident.
 *
 * THIS PANEL IS STATE, NOT IDENTITY, AND THAT DECIDES ITS COLOURS. The three
 * levels are a threshold being crossed, which is exactly what
 * `palette.success` / `palette.warning` / `palette.alert` are reserved for. So
 * they wear the semantic tokens — and every one of them ships with its label
 * beside it TWICE (on the axis and in the key underneath), because colour alone
 * is not a label and never was.
 *
 * THE CONSEQUENCE FOR THE REST OF THE SCREEN, recorded here because it is
 * invisible from anywhere else: once amber means "follow up" on this page, amber
 * is not available as a series colour anywhere on it. Nothing else reaches for
 * the semantic tokens; every categorical panel takes consecutive
 * `dataViz.series` slots, and the delta chips are deliberately verdict-free for
 * the same reason.
 *
 * Severity is NOT zero-filled by the server, so a level with nothing recorded is
 * absent rather than zero. This renderer does not invent a zero bar to fill the
 * gap, because "nothing was recorded at this level" and "this level is at zero"
 * are the same number and different claims.
 */
import React from 'react';
import { Bar, BarChart, CartesianGrid, Cell, Tooltip, XAxis, YAxis } from 'recharts';
import { AnalyticsBatchOk, AnalyticsBatchResult } from '../../../../../api/analytics-client';
import { ChartShell, ChartShellState } from '../charts/chart-shell';
import { ChartTable } from '../charts/chart-table-view';
import { DashSimpleTooltip } from '../charts/chart-tooltip';
import { severityTone } from '../charts/chart-palette';
import {
  BAR_RADIUS,
  cursor,
  format,
  grid,
  margins,
  markSeparator,
  xAxisCategory,
  yAxisCount,
  firstPaintAnimation,
} from '../charts/chart-theme';
import { KeyItem, KeyRow, KeySwatch } from './panels.styled';
import { readSlot, rowsFromItems, slotState, substitutionCaveat } from './panel-utils';
import { DrillHandlers } from '../charts/use-chart-drill';

const PANEL_ID = 'severity';

/** Level -> its display word and its blurb. Severity is a DERIVED three-member
 *  vocabulary and `meta.severity` ships the members; this maps them to a
 *  spelling and a sentence, which is the part the wire cannot carry. An unknown
 *  member falls back to a neutral, so a fourth level renders as a real bar rather
 *  than as a crash. */
const LEVEL: Record<string, { label: string; blurb: string }> = {
  ok: { label: 'OK', blurb: 'Nothing on this record needs a follow-up.' },
  follow_up: {
    label: 'Follow up',
    blurb: 'Left open, or flagged as needing somebody to come back to it.',
  },
  incident: {
    label: 'Incident',
    blurb: 'An emergency kind, a denial, or incident wording in the note.',
  },
};

/** Worst LAST, the same order the vocabulary declares. Sorting by size would put
 *  "incident" in a different place every time the range changed. */
const ORDER = ['ok', 'follow_up', 'incident'];

interface Level {
  key: string;
  label: string;
  value: number;
  tone: string;
  blurb: string;
}

export const levelsFrom = (data: AnalyticsBatchOk): Level[] =>
  [...data.items]
    .map(item => ({
      key: item.key,
      label: LEVEL[item.key]?.label || item.label || item.key,
      value: format.numberOf(item.value) ?? 0,
      tone: severityTone(item.key),
      blurb: LEVEL[item.key]?.blurb || '',
    }))
    .sort((a, b) => ORDER.indexOf(a.key) - ORDER.indexOf(b.key));

export interface SeverityPanelProps {
  state: ChartShellState;
  result?: AnalyticsBatchResult;
  drill: DrillHandlers;
  onExportPng?: (svg: SVGSVGElement | null) => void;
  onExportCsv?: () => void;
}

export const SeverityPanel: React.FC<SeverityPanelProps> = ({
  state,
  result,
  drill,
  onExportPng,
  onExportCsv,
}) => {
  const slot = readSlot(result);
  const data = slot.ok;
  const levels = data ? levelsFrom(data) : [];

  return (
    <ChartShell
      id={PANEL_ID}
      title="Severity"
      aspect={2}
      minHeight={240}
      state={slotState(state, slot)}
      notes={slot.notes}
      caveat="Severity is derived from what was written down — an emergency kind, a denial, an unclosed record, or incident wording in the note. It is not a field anybody typed."
      extraCaveat={substitutionCaveat(data)}
      emptyReason={
        slot.empty
          ? {
              title: 'No records to grade',
              text: 'Nothing was recorded in this period, so there is no severity mix.',
            }
          : null
      }
      summary={
        levels.length
          ? `Severity: ${levels
              .map(l => `${l.label} ${l.value.toLocaleString()}`)
              .join(', ')}.`
          : undefined
      }
      onExportPng={onExportPng}
      onExportCsv={onExportCsv}
      table={
        data ? (
          <ChartTable
            dimensionLabel="Severity"
            valueLabel={data.label}
            rows={rowsFromItems(data, item => LEVEL[item.key]?.label || item.label)}
            showCompare={data.compareTotal != null}
          />
        ) : undefined
      }
      controls={
        levels.length ? (
          /* THE LABEL BESIDE THE COLOUR, AGAIN. A semantic token never travels
             on its own. */
          <KeyRow data-testid="severity-key">
            {levels.map(level => (
              <KeyItem key={level.key}>
                <KeySwatch tone={level.tone} aria-hidden="true" />
                {level.label} {level.value.toLocaleString()}
              </KeyItem>
            ))}
          </KeyRow>
        ) : undefined
      }
    >
      <BarChart accessibilityLayer data={levels} margin={margins.default} barCategoryGap="28%">
        <CartesianGrid {...grid} />
        <XAxis dataKey="label" {...xAxisCategory} />
        <YAxis {...yAxisCount} />
        <Tooltip
          content={
            <DashSimpleTooltip
              tone={severityTone('incident')}
              valueKey="value"
              labelKey="label"
              noun={['record', 'records']}
              note={row => (row.blurb as string) || null}
            />
          }
          cursor={cursor.bar}
        />
        <Bar
          dataKey="value"
          radius={BAR_RADIUS}
          {...markSeparator}
          onClick={drill.onMark}
          cursor="pointer"
          {...firstPaintAnimation(PANEL_ID)}
        >
          {levels.map(level => (
            <Cell key={level.key} fill={level.tone} />
          ))}
        </Bar>
      </BarChart>
    </ChartShell>
  );
};
